import React from 'react';
import { UserPlus } from '@phosphor-icons/react/dist/ssr/UserPlus';
import { ShareNetwork } from '@phosphor-icons/react/dist/ssr/ShareNetwork';
import { ChatCircle } from '@phosphor-icons/react/dist/ssr/ChatCircle';
import { COLORS, BORDER_RADIUS } from '../../../styles/UISystem';

/**
 * FrostedPill Component
 *
 * A pill-shaped button with frosted glass effect (backdrop blur).
 * Used for floating action buttons on detail pages.
 *
 * @param {React.ReactNode} icon - Phosphor icon component (e.g., <UserPlus size={20} />)
 * @param {string} text - Optional text label
 * @param {function} onClick - Click handler function
 * @param {object} style - Additional style overrides
 * @param {boolean} disabled - Whether the button is disabled
 * @param {string} iconPosition - Position of icon: 'left' (default) or 'right'
 * @param {boolean} prominent - Use stronger shadow/border for visibility on white backgrounds
 * @param {object} textStyle - Overrides for the label span (size/weight/color) —
 *   call-site quieting without a component variant
 */
export const FrostedPill = ({
  icon,
  text,
  onClick,
  style,
  disabled = false,
  iconPosition = 'left',
  prominent = false,
  dark = false,
  color = null,
  textStyle = null,
  ...props
}) => {
  // Determine padding based on content
  // Icon-only: square-ish padding
  // Icon + text: more horizontal padding
  const hasText = Boolean(text);
  const padding = hasText ? '10px 16px' : '10px 14px';

  // Style variations based on prominent and dark props
  let backgroundColor, border, boxShadow, textColor;

  if (disabled) {
    backgroundColor = 'rgba(200, 200, 200, 0.3)';
    border = '1px solid rgba(255, 255, 255, 0.5)';
    boxShadow = '0 1px 4px rgba(0, 0, 0, 0.06)';
    textColor = '#BBBBBB';
  } else if (dark) {
    // Dark variant: black background, white text
    backgroundColor = '#1C1C1E';
    border = 'none';
    boxShadow = '0 2px 12px rgba(0, 0, 0, 0.25)';
    textColor = '#FFFFFF';
  } else if (prominent) {
    backgroundColor = 'rgba(255, 255, 255, 0.6)';
    border = '1px solid rgba(0, 0, 0, 0.08)';
    boxShadow = '0 2px 12px rgba(0, 0, 0, 0.15)';
    textColor = '#999999';
  } else {
    backgroundColor = 'rgba(255, 255, 255, 0.3)';
    border = '1px solid rgba(255, 255, 255, 0.5)';
    boxShadow = '0 1px 4px rgba(0, 0, 0, 0.06)';
    textColor = '#999999';
  }

  return (
    <div
      onClick={disabled ? undefined : onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: hasText ? '8px' : '0',
        padding,
        borderRadius: BORDER_RADIUS.PILL,
        backgroundColor,
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border,
        boxShadow,
        cursor: disabled ? 'default' : 'pointer',
        whiteSpace: 'nowrap',
        ...style,
      }}
      {...props}
    >
      {icon && iconPosition === 'left' && (
        <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>
      )}
      {text && (
        <span
          style={{
            fontSize: '16px',
            color: color || textColor,
            fontWeight: '400',
            ...textStyle,
          }}
        >
          {text}
        </span>
      )}
      {icon && iconPosition === 'right' && (
        <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>
      )}
    </div>
  );
};

/**
 * FrostedPillExample Component
 *
 * Example usage of the FrostedPill component for UI Showcase.
 */
export const FrostedPillExample = () => {
  // Icons imported at top of file

  const handleClick = () => {
    console.log('FrostedPill clicked');
  };

  return (
    <div style={{ marginBottom: '60px' }}>
      <div
        style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: COLORS.DARK_GRAY,
          marginBottom: '32px',
          textAlign: 'center',
        }}
      >
        FrostedPill
      </div>

      {/* Show on a gradient background so the frosted effect is visible */}
      <div
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '40px 20px',
          borderRadius: '16px',
          marginBottom: '16px',
        }}
      >
        <div
          style={{
            fontSize: '14px',
            color: 'white',
            marginBottom: '16px',
            textAlign: 'center',
            opacity: 0.8,
          }}
        >
          Icon + Text
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '24px',
          }}
        >
          <FrostedPill
            icon={<UserPlus size={20} color="#999999" weight="regular" />}
            text="Add to contacts"
            onClick={handleClick}
          />
        </div>

        <div
          style={{
            fontSize: '14px',
            color: 'white',
            marginBottom: '16px',
            textAlign: 'center',
            opacity: 0.8,
          }}
        >
          Icon Only
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '12px',
            marginBottom: '24px',
          }}
        >
          <FrostedPill
            icon={<ShareNetwork size={18} color="#999999" weight="regular" />}
            onClick={handleClick}
          />
          <FrostedPill
            icon={<UserPlus size={20} color="#999999" weight="regular" />}
            onClick={handleClick}
          />
          <FrostedPill
            icon={<ChatCircle size={20} color="#999999" weight="regular" />}
            onClick={handleClick}
          />
        </div>

        <div
          style={{
            fontSize: '14px',
            color: 'white',
            marginBottom: '16px',
            textAlign: 'center',
            opacity: 0.8,
          }}
        >
          Text Only
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '24px',
          }}
        >
          <FrostedPill text="Social" onClick={handleClick} />
        </div>

        <div
          style={{
            fontSize: '14px',
            color: 'white',
            marginBottom: '16px',
            textAlign: 'center',
            opacity: 0.8,
          }}
        >
          Icon + Count (like Share button)
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '24px',
          }}
        >
          <FrostedPill
            icon={<ShareNetwork size={18} color="#999999" weight="regular" />}
            text="3"
            onClick={handleClick}
          />
        </div>

        <div
          style={{
            fontSize: '14px',
            color: 'white',
            marginBottom: '16px',
            textAlign: 'center',
            opacity: 0.8,
          }}
        >
          Disabled
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <FrostedPill
            icon={<UserPlus size={20} color="#BBBBBB" weight="regular" />}
            text="Add to contacts"
            disabled
          />
        </div>
      </div>

      <div
        style={{
          fontSize: '12px',
          color: COLORS.MEDIUM_GRAY,
          textAlign: 'center',
          lineHeight: '1.5',
        }}
      >
        Frosted glass effect with backdrop blur.
        <br />
        Used for floating actions on detail pages.
      </div>
    </div>
  );
};
